// Roster spatial-first tours (U10 — PARTIAL scaffolding):
// "Needs tagging" sidebar for the suggestions panel. Lists services the
// backend's spatial-first clusterer dropped because they have no resolvable
// touring_identity. Operator tags inline (same PATCH as the AdminLoadIn
// dropdown) without leaving the suggestions view; on success the parent
// re-fetches /api/feed-suggestions and the tagged service slides out of
// the sidebar into the appropriate StrategyCard spatial group.
//
// U10 is PARTIAL because the `untagged_services` field on the suggestions
// response is added by U7 (BLOCKED-BY the v0 plan). Until U7 ships:
//   - The parent SuggestionsView never receives a populated
//     `untagged_services` array, so the sidebar never appears.
//   - The dropdown's vocab + tier-aware behavior is fully wired and
//     reuses VocabularyInlineAddDialog from U5.
// When U7 lands, this component becomes live with zero further changes
// in this file.
//
// One-off events (window.tour_id IS NULL): no tour vocabulary exists, so
// we fall back to a free-text service_location_label edit per row using
// the existing PATCH /api/memberships/{id}/service-location endpoint —
// matching the F3 flow in the plan.

import { useState } from "react";
import {
  api,
  ApiError,
  setTouringIdentity,
} from "@/lib/api";
import type {
  TourVocabularyEntry,
  UntaggedService,
} from "@/lib/types";
import { VocabularyInlineAddDialog } from "@/components/VocabularyInlineAddDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const ADD_NEW_SENTINEL = "__ADD_NEW__";

interface Props {
  untaggedServices: UntaggedService[];
  // When null, the event has no tour — render a service_location_label
  // editor per row instead of a touring_identity dropdown.
  tourId: string | null;
  tourVocabulary: TourVocabularyEntry[];
  // Called after a successful PATCH so the parent can re-fetch
  // /api/feed-suggestions (tagged service should leave the sidebar).
  onTagged: () => void;
  // Called when the operator added a new vocabulary entry inline. The parent
  // re-fetches the tour so the dropdown options pick up the new label.
  onVocabRefresh: () => Promise<void>;
}

export function NeedsTaggingSidebar({
  untaggedServices,
  tourId,
  tourVocabulary,
  onTagged,
  onVocabRefresh,
}: Props) {
  if (untaggedServices.length === 0) return null;

  return (
    <Card className="border-amber-500/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          Needs tagging
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
            {untaggedServices.length}
          </span>
        </CardTitle>
        <div className="text-xs text-muted-foreground">
          {tourId
            ? "Pick a touring identity so the clusterer can place these services."
            : "Set a service location so the clusterer can place these services."}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {untaggedServices.map((svc) =>
          tourId
            ? (
              <TourTaggingRow
                key={svc.membership_id}
                service={svc}
                tourId={tourId}
                tourVocabulary={tourVocabulary}
                onTagged={onTagged}
                onVocabRefresh={onVocabRefresh}
              />
            )
            : (
              <ServiceLocationRow
                key={svc.membership_id}
                service={svc}
                onSaved={onTagged}
              />
            )
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Row variants
// ---------------------------------------------------------------------------

function TourTaggingRow({
  service, tourId, tourVocabulary, onTagged, onVocabRefresh,
}: {
  service: UntaggedService;
  tourId: string;
  tourVocabulary: TourVocabularyEntry[];
  onTagged: () => void;
  onVocabRefresh: () => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Track the just-picked value locally so the select reflects the operator's
  // choice while the parent re-fetch is in flight. Once the parent's fetch
  // resolves, this row will leave the sidebar entirely so persistence isn't
  // needed.
  const [pending, setPending] = useState<string | null>(null);

  const commitTag = async (label: string | null) => {
    setSaving(true);
    try {
      await setTouringIdentity(service.membership_id, label);
      setPending(label);
      onTagged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        const body = e.body as { error?: string } | undefined;
        if (body?.error === "identity_not_in_tour_vocabulary") {
          alert(
            "That identity is no longer in the tour vocabulary — refresh and pick again.",
          );
          void onVocabRefresh();
          return;
        }
        alert(`Couldn't tag service: ${body?.error ?? "validation failed"}`);
        return;
      }
      alert(`Failed to tag service: ${e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === ADD_NEW_SENTINEL) {
      setDialogOpen(true);
      return;
    }
    void commitTag(v === "" ? null : v);
  };

  return (
    <div className="rounded border border-border/60 p-2 space-y-1.5">
      <div className="text-sm">
        <div className="font-medium truncate" title={service.service_name ?? ""}>
          {service.service_name ?? "(unnamed service)"}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {service.service_location_label ?? "(no location)"}
        </div>
      </div>
      <select
        value={pending ?? ""}
        disabled={saving}
        onChange={handleChange}
        className="h-8 px-2 text-sm rounded bg-background border border-input w-full"
      >
        <option value="">(none — needs tagging)</option>
        {tourVocabulary.map((entry) => (
          <option key={entry.label} value={entry.label}>
            {entry.label} · {entry.tier}
          </option>
        ))}
        <option value={ADD_NEW_SENTINEL}>+ Add new identity to tour…</option>
      </select>

      {dialogOpen && (
        <VocabularyInlineAddDialog
          tourId={tourId}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onAdded={async (entry) => {
            await onVocabRefresh();
            void commitTag(entry.label);
          }}
        />
      )}
    </div>
  );
}

function ServiceLocationRow({
  service, onSaved,
}: {
  service: UntaggedService;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(service.service_location_label ?? "");
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    const trimmed = draft.trim();
    const next = trimmed === "" ? null : trimmed;
    // Avoid no-op PATCH if the operator hit blur without changes.
    if (next === (service.service_location_label ?? null)) return;
    setSaving(true);
    try {
      await api.patch(
        `/api/memberships/${encodeURIComponent(service.membership_id)}/service-location`,
        { service_location_label: next },
      );
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        const body = e.body as { error?: string } | undefined;
        alert(`Couldn't set location: ${body?.error ?? "validation failed"}`);
      } else {
        alert(`Failed to set service location: ${e}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded border border-border/60 p-2 space-y-1.5">
      <div className="text-sm">
        <div className="font-medium truncate" title={service.service_name ?? ""}>
          {service.service_name ?? "(unnamed service)"}
        </div>
      </div>
      <input
        type="text"
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          }
        }}
        placeholder="e.g. FOH, Stage L Sub"
        className="h-8 px-2 text-sm rounded bg-background border border-input w-full"
      />
    </div>
  );
}
